import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './LessonProgress.module.scss';
import Header from '../../components/header/Header';
import Button from '../../components/button/Button';
import Spinner from '../../components/spinner/Spinner';
import Footer from '../../components/footer/Footer';
import Card from './card/Card';
import { LessonInProgress, useListLessonsInProgressLazyQuery, useResetAllLessonProgressMutation, useResetLessonProgressMutation } from '@src/api/graphql-codegen';
import { Pagination } from "antd";
import { useToast } from '@src/hooks';
import UnitProgressModal from '@src/components/unit-progress-modal/UnitProgressModal';
import LessonProgressModal from '@src/components/lesson-progress-modal/LessonProgressModal';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { IconInfo, IconReset } from '@src/constants';
import NormalModal from '@src/components/modal/normal-modal/NormalModal';
import IconWarning from '@src/assets/icons/modal-warning.svg?react';
import { TextInput } from '@src/components/text-input/TextInput';
import { logger } from '../../utils/logger';
import FooterPage from '@src/components/footer-page/FooterPage';

const pageSize = 10;

function LessonProgress() {
  const [current, setCurrent] = useState(1);
  const [minIndex, setMinIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(pageSize);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetText, setResetText] = useState('');
  const [resetAllLesson, setResetAllLesson] = useState(false);
  const [resetLesson, setResetLesson] = useState(false);
  const [resetProgress, setResetProgress] = useState<LessonInProgress | undefined>(undefined)
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [unitId, setUnitId] = useState<string | undefined>(undefined)
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [lessonId, setLessonId] = useState<string | undefined>(undefined)

  const {successToast, errorToast} = useToast();

  const [listLessonsInProgress, {data, loading, refetch}] = useListLessonsInProgressLazyQuery({fetchPolicy: 'network-only'})
  const [resetAllLessonProgress, {loading: deleting}] = useResetAllLessonProgressMutation({
    onCompleted(res) {
      if (res.resetAllLessonProgress) {
        successToast(`Lesson Progress has been Reset!`);
        refetch();
      }
      setResetAllLesson(false)
    },
    onError(e) {
      logger.info('remove Error', e.message)
      errorToast(e.message);
    }
  })
  const [resetLessonProgress, {loading: deleting1}] = useResetLessonProgressMutation({
    onCompleted(res) {
      if (res.resetLessonProgress) {
        successToast(`Lesson Progress has been Reset!`);
        refetch();
      }
      setResetLesson(false)
      setResetProgress(undefined)
    },
    onError(e) {
      logger.info('remove Error', e.message)
      errorToast(e.message);
    }
  })

  useEffect(() => {
    listLessonsInProgress()
  }, [listLessonsInProgress])
  const lessons = useMemo(() => {
    return data?.listLessonsInProgress || [];
  }, [data?.listLessonsInProgress]);

  const handleResetAllProgress = useCallback(() => {
    resetAllLessonProgress();
  },[resetAllLessonProgress]);

  const handleOpenConfirmReset = () => setConfirmReset(true)
  const handleOpenResetModal = () => {
    setConfirmReset(false)
    setResetAllLesson(true)
  }

  const handleChangePage = (page: number) => {
    setCurrent(page);
    setMinIndex((page-1) * pageSize);
    setMaxIndex(page * pageSize);
  }

  const handleOpenLesson = useCallback((progress: LessonInProgress) => {
    if (progress.unitId) {
      setUnitId(progress.unitId)
      setShowUnitModal(true)
    } else {
      if (progress.lessonId) {
        setLessonId(progress.lessonId)
        setShowLessonModal(true)
      }
    }
  }, [])

  const handleResetLesson = useCallback(() => {
    if (resetProgress?.lessonId) {
      resetLessonProgress({
        variables: {
          input: {
            unitId: resetProgress?.unitId,
            lessonId: resetProgress?.lessonId
          }
        }
      })
    }
  },[resetProgress, resetLessonProgress])

  const openResetLessonModal = useCallback((progress: LessonInProgress) => {
    setResetProgress(progress)
    setResetLesson(true)
  },[])

  const handleCloseResetModal = () => {
    setResetLesson(false)
  }

  const handleCloseUnitModal = useCallback(() => {
    setShowUnitModal(false);
    refetch()
  }, [refetch])

  const handleCloseLessonModal = useCallback(() => {
    setShowLessonModal(false);
    refetch()
  }, [refetch])

  const handleChangeReset = useCallback((value: any) => {
    setResetText(value)
  }, []);

  return (
    <>
      <Header/>
      <div className={styles.page}>
        {loading ? <div className={styles.spinner}><Spinner/></div> : <>
        <div className={styles.header}>
          <div className={styles.titleView}>
            <div className={styles.title}>{'Lesson Progress'}</div>
            {<>
            <span className={styles.description}>View In Progress Lessons and reset the Progress Status on one or more of your Lessons below. This will clear Completed Lessons, and reset the Progress Status indicators on each Lesson, Unit and Course.</span>
            </>}
          </div>
          <div>
            <Button disabled={lessons.length === 0} color='red' className={styles.resetAllButton} size={'small'} label={'Reset All Lessons'} onClick={handleOpenConfirmReset} rightIcon={<div className={styles.resetIcon}><LordIcon src={IconReset} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} size={30} target='.LessonProgress_resetAllButton__QMnrc'/></div>}/>
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.headerFilter}>
            <div className={styles.filter}>
              <div className={styles.headerTitle}>Grade</div>
            </div>
            <div className={styles.filter}>
              <div className={styles.headerTitle}>Lesson Name</div>
            </div>
            <div className={styles.filter}>
              <div className={styles.headerTitle}>Progress</div>
            </div>
            <div className={styles.resetFilter}>
              <div className={styles.headerTitle}>Reset</div>
            </div>
          </div>
          {lessons.map((item, index) => 
            index >= minIndex &&
            index < maxIndex && (  
              <Card key={index} lesson={item} resetProgress={openResetLessonModal} openLesson={handleOpenLesson} loading={deleting1}/>
            )
          )}
          <Pagination
            pageSize={pageSize}
            current={current}
            total={lessons.length}
            onChange={handleChangePage}
            className={styles.pagination}
            showSizeChanger={false}
          />
        </div>
        </>}
        <FooterPage />
      </div>
      {/* <Footer/> */}
      {confirmReset ? <NormalModal 
        logoIcon={<IconWarning />} title={'WARNING!'} open={confirmReset} onClose={()=>{setConfirmReset(false)}} loading={deleting}
        description={`This will clear all Completed Lessons, and reset the Progress Status indicators on every Lesson, Unit and Course. This cannot be undone`}
        leftButton={<Button size='small' color='green' className={styles.cancelButton} disabled={deleting} onClick={()=>{setConfirmReset(false)}} label={'CANCEL'}/>}
        rightButton={<Button size='small' color='red' className={styles.resetButton} disabled={deleting} onClick={handleOpenResetModal} label={'RESET PROGRESS'} rightIcon={<div className={styles.infoIcon}><LordIcon src={IconInfo} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} size={23.4}/></div>}/>}
      /> : null}
      {resetAllLesson ? <NormalModal 
        logoIcon={<IconWarning className={styles.redWarning} />} title={<div className={styles.redWarningTitle}>WARNING!</div>} open={resetAllLesson} onClose={()=>{setResetAllLesson(false)}} loading={deleting}
        description={
          <div className={styles.redDetail}>
            Please type the characters “RESET”.  This cannot be undone.
            <TextInput className={styles.resetInput} id='resetInput' label='' value={resetText} placeholder='Please type the characters “RESET”' onChange={handleChangeReset} />
          </div>
        }
        leftButton={<Button size='small' color='green' className={styles.cancelButton} disabled={deleting} onClick={()=>{setResetAllLesson(false)}} label={'CANCEL'}/>}
        rightButton={<Button size='small' color='red' className={styles.resetButton} disabled={deleting || resetText !== 'RESET'} onClick={handleResetAllProgress} label={'Reset All Progress'} rightIcon={<div className={styles.infoIcon}><LordIcon src={IconInfo} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} size={23.4}/></div>}/>}
      /> : null}
      {resetLesson ? <NormalModal 
        logoIcon={<IconWarning />} title={'Reset Lesson Progress?'} open={resetLesson} onClose={handleCloseResetModal} loading={deleting1}
        description={<div className={styles.detail}>You're about to reset the lesson status for <strong>{resetProgress?.lessonName}</strong>. This cannot be undone. Do you want to continue?</div>}
        leftButton={<Button size='small' color='green' className={styles.cancelButton} disabled={deleting1} onClick={handleCloseResetModal} label={'CANCEL'}/>}
        rightButton={<Button size='small' color='red' className={styles.resetButton} disabled={deleting1} onClick={handleResetLesson} label={'RESET PROGRESS'} rightIcon={<div className={styles.infoIcon}><LordIcon src={IconInfo} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} size={23.4}/></div>}/>}
      /> : null}
      {(showUnitModal && unitId) && <UnitProgressModal open={showUnitModal} onClose={handleCloseUnitModal} unitId={unitId} />}
      {(showLessonModal && lessonId) && <LessonProgressModal open={showLessonModal} onClose={handleCloseLessonModal} lessonId={lessonId} lessonlist={[]}/>}
    </>
  );
}

export default LessonProgress;
